/**
 * Stage labels, CTAs, sidebar sub-labels — shared between the PropertyCard,
 * deal-room sidebar, and pill nav.
 */

import { cashReadinessStore } from '@/utils/propertyView/localStateStore';
import type { PropertyStage, PropertyView } from '@/types/PropertyView';

export const STAGE_LABELS: Record<PropertyStage, string> = {
  1: 'Shop',
  2: 'Qualify',
  3: 'Apply',
  4: 'Tour',
  5: 'Offer Submission',
  6: 'Negotiate',
  7: 'Processing',
  8: 'Underwriting',
  9: 'Closing',
};

export const STAGE_DESCRIPTIONS: Record<PropertyStage, string> = {
  1: 'Saved property — get a payment estimate and explore programs.',
  2: 'Qualified — generate a pre-qualification letter for this property.',
  3: 'Applied — complete your application and upload required documents.',
  4: 'Tour — log tour notes and confirm post-tour readiness.',
  5: 'Offer submission — build and submit your offer.',
  6: 'Negotiating — respond to counteroffers and finalize terms.',
  7: 'Processing — title, appraisal, inspection, and document checks.',
  8: 'Underwriting — respond to underwriter conditions.',
  9: 'Closing — review CD, verify wire instructions, sign.',
};

export const STAGE_CTA: Record<PropertyStage, string> = {
  1: 'Run qualification check →',
  2: 'Start your application →',
  3: 'Upload remaining documents →',
  4: 'Schedule a tour →',
  5: 'Submit offer →',
  6: 'Submit counteroffer terms →',
  7: 'Check processing checklist →',
  8: 'Respond to open conditions →',
  9: 'Review closing disclosure →',
};

export const STAGE_CTA_REFI: Partial<Record<PropertyStage, string>> = {
  3: 'Complete your application →',
  7: 'Check document checklist →',
};

export const NEXT_STEP_LABEL: Record<PropertyStage, string> = {
  1: 'Next Step: Qualified',
  2: 'Next Step: Applied',
  3: 'Next Step: Tour',
  4: 'Next Step: Offer Submission',
  5: 'Next Step: Negotiate',
  6: 'Next Step: Processing',
  7: 'Next Step: Underwriting',
  8: 'Next Step: Closing',
  9: 'Mark as Closed',
};

export const NEXT_STEP_LABEL_REFI: Partial<Record<PropertyStage, string>> = {
  3: 'Next Step: Processing',
};

export const SELLER_STAGE_LABELS: Partial<Record<PropertyStage, string>> = {
  1: 'Information validation',
  2: 'Negotiation',
  3: 'Ratification',
  4: 'Inspections',
  5: 'Escrow',
  6: 'Addendums',
  7: 'Closing',
};

export const SELLER_STAGE_DESCRIPTIONS: Partial<Record<PropertyStage, string>> =
  {
    1: 'Complete seller information, disclosures, and property details.',
    2: 'Review the offer and negotiate contract terms with the buyer.',
    3: 'Sign the agreed contract when all parties are ready.',
    4: 'Track the inspection period and property inspection activity.',
    5: 'Coordinate timeline, tasks, documents, and buyer communication.',
    6: 'Manage addendums and renegotiations during escrow.',
    7: 'Complete closing tasks and finalize the sale.',
  };

export const SELLER_STAGE_CTA: Partial<Record<PropertyStage, string>> = {
  1: 'Complete seller details →',
  2: 'Open negotiation →',
  3: 'Review signatures →',
  4: 'View inspections →',
  5: 'Open escrow dashboard →',
  6: 'Review addendums →',
  7: 'Review closing checklist →',
};

export const SELLER_NEXT_STEP_LABEL: Partial<Record<PropertyStage, string>> = {
  1: 'Next Step: Negotiation',
  2: 'Next Step: Ratification',
  3: 'Next Step: Inspections',
  4: 'Next Step: Escrow',
  5: 'Next Step: Addendums',
  6: 'Next Step: Closing',
  7: 'Mark as Closed',
};

export function isSellerPipeline(property: PropertyView): boolean {
  return property.pipelineMode === 'seller';
}

export function stageLabelFor(
  property: PropertyView,
  stage: PropertyStage
): string {
  if (isSellerPipeline(property)) {
    return SELLER_STAGE_LABELS[stage] ?? `Stage ${stage}`;
  }
  return STAGE_LABELS[stage];
}

export function stageDescriptionFor(property: PropertyView): string {
  if (isSellerPipeline(property)) {
    return (
      SELLER_STAGE_DESCRIPTIONS[property.stage] ??
      SELLER_STAGE_LABELS[property.stage] ??
      ''
    );
  }
  return STAGE_DESCRIPTIONS[property.stage];
}

/** Returns the user-facing CTA for the card and the stage panel. */
export function ctaForProperty(property: PropertyView): string {
  if (isSellerPipeline(property)) {
    return SELLER_STAGE_CTA[property.stage] ?? 'Continue →';
  }
  if (property.isRefinance && STAGE_CTA_REFI[property.stage]) {
    return STAGE_CTA_REFI[property.stage]!;
  }
  return STAGE_CTA[property.stage];
}

export function nextStepLabel(property: PropertyView): string {
  if (isSellerPipeline(property)) {
    return SELLER_NEXT_STEP_LABEL[property.stage] ?? 'Next Step';
  }
  if (property.isRefinance && NEXT_STEP_LABEL_REFI[property.stage]) {
    return NEXT_STEP_LABEL_REFI[property.stage]!;
  }
  return NEXT_STEP_LABEL[property.stage];
}

/**
 * Sub-label rendered under the stage name in the sidebar.
 * Returns undefined for stages without a contextual sub-label.
 */
export function stageSubLabel(
  stage: PropertyStage,
  property: PropertyView
): string | undefined {
  if (isSellerPipeline(property)) {
    switch (stage) {
      case 1:
        return property.sellerDetailsComplete
          ? 'Details complete'
          : 'Add Details pending';
      case 2:
        if (property.offerSubmittedPrice) {
          return `Offer at $${property.offerSubmittedPrice.toLocaleString()}`;
        }
        return property.contractStatus?.replace(/_/g, ' ');
      case 3:
        if (property.contractRatified) return 'Ratified';
        return 'Signatures pending';
      case 5:
        return property.contractStatus?.replace(/_/g, ' ');
      case 7:
        if (property.closingDate) return `Closing ${property.closingDate}`;
        return undefined;
      default:
        return undefined;
    }
  }
  switch (stage) {
    case 2: {
      const hasLetter = Boolean(property.preQualLetterId);
      return hasLetter
        ? 'Pre-qual letter generated'
        : 'Pre-qual letter pending';
    }
    case 3: {
      const total = property.checklistRequiredTotal ?? 7;
      const done = property.checklistRequiredCompleted ?? 0;
      if (done >= total && total > 0) return 'Application complete';
      return `${done} of ${total} docs uploaded`;
    }
    case 5:
      if (property.offerSubmittedPrice) {
        return `Offer at $${property.offerSubmittedPrice.toLocaleString()}`;
      }
      return undefined;
    case 6:
      if (property.offerStatus === 'accepted') return 'Accepted';
      if (
        property.contractStatusHistory &&
        property.contractStatusHistory.length
      ) {
        return `${property.contractStatusHistory.length} updates`;
      }
      return undefined;
    case 7:
      if (property.processingChecklistPct !== undefined) {
        return `${Math.round(property.processingChecklistPct * 100)}% complete`;
      }
      return undefined;
    case 8:
      if (property.uwConditionsOpen !== undefined) {
        return property.uwConditionsOpen === 0
          ? 'Clear to close ✓'
          : `${property.uwConditionsOpen} conditions open`;
      }
      return undefined;
    case 9:
      if (property.closingDate) {
        return `Closing ${property.closingDate}`;
      }
      return undefined;
    default:
      return undefined;
  }
}

/** Loan-type pill display. */
export function loanTypeBadgeLabel(property: PropertyView): {
  label: string;
  color: 'navy' | 'teal' | 'amber';
} | null {
  if (isSellerPipeline(property)) return null;
  if (property.isDscr) {
    return { label: 'Investment', color: 'amber' };
  }
  if (property.isRefinance) {
    return { label: 'Refinance', color: 'teal' };
  }
  return { label: 'Purchase', color: 'navy' };
}

/**
 * Stage completion requirements (spec §19). Result is consumed by the
 * NextStepCTA — `satisfied: false` disables the button and surfaces the
 * `missing` list in a tooltip.
 */
export type StageRequirementFn = (property: PropertyView) => {
  satisfied: boolean;
  missing: string[];
};

export const STAGE_REQUIREMENTS: Record<PropertyStage, StageRequirementFn> = {
  1: () => ({ satisfied: true, missing: [] }),
  2: (p) => {
    const missing: string[] = [];
    if (!p.loanProgram) missing.push('Select a loan program');
    if (p.downPaymentAmt === undefined) missing.push('Set a down payment');
    if (!p.interestRate && p.estimatedMonthlyPayment === undefined) {
      // Either having a configured rate or a complete payment estimator is fine.
    }
    return { satisfied: missing.length === 0, missing };
  },
  3: (p) => {
    const missing: string[] = [];
    const isCash =
      String(p.loanProgram || '')
        .toLowerCase()
        .trim() === 'cash';
    if (isCash) {
      if (!cashReadinessStore.isComplete(p.id)) {
        missing.push('Confirm cash purchase readiness checklist');
      }
      return { satisfied: missing.length === 0, missing };
    }
    if (!p.applicationId) missing.push('Start a loan application');
    const total = p.checklistRequiredTotal ?? 0;
    const done = p.checklistRequiredCompleted ?? 0;
    if (total > 0 && done < total) {
      missing.push(`Upload ${total - done} remaining document(s)`);
    }
    const ratio =
      p.applicationSectionsTotal && p.applicationSectionsComplete !== undefined
        ? p.applicationSectionsComplete / p.applicationSectionsTotal
        : 0;
    if (ratio < 0.9) missing.push('Finish all application sections');
    return { satisfied: missing.length === 0, missing };
  },
  4: (p) => {
    const missing: string[] = [];
    if (!p.tourLogged) missing.push('Log at least one tour');
    return { satisfied: missing.length === 0, missing };
  },
  5: (p) => {
    const missing: string[] = [];
    if (!p.contractId) missing.push('Start your offer');
    if (!p.offerStatus) missing.push('Submit your offer');
    return { satisfied: missing.length === 0, missing };
  },
  6: (p) => {
    const missing: string[] = [];
    if (p.offerStatus !== 'accepted' && !p.contractRatified) {
      missing.push('Reach an accepted offer');
    }
    return { satisfied: missing.length === 0, missing };
  },
  7: (p) => {
    const missing: string[] = [];
    if (
      p.processingChecklistPct !== undefined &&
      p.processingChecklistPct < 0.8
    ) {
      missing.push('Complete at least 80% of the processing checklist');
    }
    return { satisfied: missing.length === 0, missing };
  },
  8: (p) => {
    const missing: string[] = [];
    if (p.uwConditionsOpen === undefined) {
      return { satisfied: true, missing: [] };
    }
    if (p.uwConditionsOpen > 0) {
      missing.push(`Clear ${p.uwConditionsOpen} open condition(s)`);
    }
    return { satisfied: missing.length === 0, missing };
  },
  9: () => ({ satisfied: true, missing: [] }),
};

export const SELLER_STAGE_REQUIREMENTS: Partial<
  Record<PropertyStage, StageRequirementFn>
> = {
  1: (p) => ({
    satisfied: Boolean(p.sellerDetailsComplete),
    missing: p.sellerDetailsComplete ? [] : ['Complete seller information'],
  }),
  2: (p) => ({
    satisfied:
      p.contractRatified === true ||
      p.offerStatus === 'accepted' ||
      (p.stage ?? 0) > 2,
    missing: ['Reach agreement on offer terms'],
  }),
  3: (p) => ({
    satisfied: Boolean(p.contractRatified),
    missing: ['Complete contract signatures'],
  }),
  4: () => ({ satisfied: true, missing: [] }),
  5: () => ({ satisfied: true, missing: [] }),
  6: () => ({ satisfied: true, missing: [] }),
  7: () => ({ satisfied: true, missing: [] }),
};

export function stageRequirementsFor(
  property: PropertyView,
  stage: PropertyStage
): ReturnType<StageRequirementFn> {
  if (isSellerPipeline(property)) {
    const fn = SELLER_STAGE_REQUIREMENTS[stage];
    return fn ? fn(property) : { satisfied: true, missing: [] };
  }
  return STAGE_REQUIREMENTS[stage](property);
}

export function stageRequirements(
  property: PropertyView
): ReturnType<StageRequirementFn> {
  return stageRequirementsFor(property, property.stage);
}

/** Helper used by the sidebar to know if a stage is clickable. */
export function isStageCompleted(
  stage: PropertyStage,
  property: PropertyView
): boolean {
  return stage < property.stage;
}

export function isStageActive(
  stage: PropertyStage,
  property: PropertyView
): boolean {
  return stage === property.stage;
}

export function isStageLocked(
  stage: PropertyStage,
  property: PropertyView
): boolean {
  return stage > property.stage;
}
